import { openDB } from "idb";
import {
  WORKOUT_LOG_DB_NAME,
  WORKOUT_LOG_DB_VERSION,
} from "../store/dbSchema";
import { LocalExecutionStore } from "../store/localExecutionStore";
import { defaultRecoveryStorage, isLocalRecoverySuppressed } from "./recoverySuppression";

// Bounded well below any bootstrap timing: a probe that has not answered by
// then resolves to "no recovery" and startup carries on (§9.8, H8-10).
const LOCAL_RECOVERY_PROBE_TIMEOUT_MS = 1500;
const OPEN_STATUSES = Object.freeze(["ACTIVE", "FINISHED"]);

function startedAtTime(record) {
  return new Date(record.server?.startedAt);
}

// The local open-execution order TrainHub recovers from: ACTIVE/FINISHED,
// newest server.startedAt first. The probe uses the same function, so launch
// recovery and the Hub always agree on which record comes first.
export function selectLocalOpenExecutions(records = []) {
  return records
    .filter((record) => OPEN_STATUSES.includes(record.document?.status))
    .sort((left, right) => startedAtTime(right) - startedAtTime(left));
}

function isPendingSaved(record) {
  return record.document?.status === "SAVED" &&
    Number(record.localRevision || 0) > Number(record.ackedRevision || 0);
}

// §9.8 record selection. ACTIVE/FINISHED first; otherwise the newest SAVED
// record that still has local changes to sync. An acknowledged SAVED record
// and any DISCARDED record (pending or not) never create a recovery surface.
export function selectLocalRecoveryTarget(userId, records = []) {
  const own = (records || []).filter((record) => record?.userId === userId);
  const target = selectLocalOpenExecutions(own)[0] ||
    own.filter(isPendingSaved).sort((left, right) => startedAtTime(right) - startedAtTime(left))[0];
  if (!target) return null;
  // Metadata only: never the record itself or any of its content.
  return {
    userId,
    executionId: target.executionId,
    status: target.document.status,
  };
}

// Opens the existing database for reading only. It never creates, upgrades or
// downgrades it: a missing database aborts its creation, and a newer version
// fails the open with VersionError. Both resolve to "no recovery".
function readOnlyOpener(openDb, name) {
  let opening = null;
  const open = () => {
    if (!opening) {
      opening = openDb(name, WORKOUT_LOG_DB_VERSION, {
        upgrade(_database, _oldVersion, _newVersion, transaction) {
          transaction.done?.catch?.(() => {});
          transaction.abort();
        },
        blocking(_currentVersion, _blockedVersion, event) {
          event?.target?.close?.();
        },
      });
    }
    return opening;
  };
  const close = () => {
    if (!opening) return;
    opening.then((database) => database?.close?.(), () => {});
  };
  return { open, close };
}

/**
 * Local recovery probe (§9.8). Read-only, time-bounded and fail-closed.
 *
 * Reads `meta.lastAuthenticatedUserId` and only that user's executions, and
 * resolves to `{ userId, executionId, status }` or `null`. A user who
 * explicitly logged out here (`recoverySuppression`) is never recovered. It never writes,
 * finalizes, reconciles, syncs or takes an editor lock, and it never rejects.
 */
export async function probeLocalRecovery({
  openDb = openDB,
  name = WORKOUT_LOG_DB_NAME,
  timeoutMs = LOCAL_RECOVERY_PROBE_TIMEOUT_MS,
  indexedDBAvailable = typeof indexedDB !== "undefined",
  suppressionStorage = defaultRecoveryStorage(),
} = {}) {
  if (!indexedDBAvailable) return null;
  const connection = readOnlyOpener(openDb, name);
  // The store's own read methods, over a connection that cannot write the
  // schema. Only getMeta and listExecutions are ever called on it.
  const store = new LocalExecutionStore({ openDb: connection.open });
  let timer = null;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => resolve(null), timeoutMs);
  });
  const read = (async () => {
    const userId = await store.getLastAuthenticatedUserId();
    if (!userId || typeof userId !== "string") return null;
    // A successful explicit logout stays authoritative, even if its cleanup
    // left this hint and the user's records behind.
    if (isLocalRecoverySuppressed(userId, suppressionStorage)) return null;
    return selectLocalRecoveryTarget(userId, await store.listExecutions(userId));
  })().catch(() => null);
  try {
    return await Promise.race([read, timeout]);
  } catch (_error) {
    return null;
  } finally {
    clearTimeout(timer);
    // Also closes a connection that only opens after a timeout.
    connection.close();
  }
}

export { LOCAL_RECOVERY_PROBE_TIMEOUT_MS };
